import axios from 'axios';
import utils from '../../common/utils.js';
import cache from './cache.js';
import config from '../../common/config.js';
import dayjs from 'dayjs';
import WidgetOptions from '../../common/WidgetOptions.js';

cache.taxonomies = {};

// limit how many queries we cache, the results can add up in memory

const LANGUAGES = WidgetOptions.options.languages;

export const GLOBAL_TAXONOMY_FILES = {
	t001: 'terms-options-t001.json',
	cdc_topics: 'terms-options-t001.json',
	a003: 'terms-options-a003.json',
	cdc_audiences: 'terms-options-a003.json',
	p002: 'terms-options-p002.json',
	cdc_populations: 'terms-options-p002.json',
	s004: 'terms-options-s004.json',
	cdc_settings: 'terms-options-s004.json',
	c005: 'terms-options-c005.json',
	cdc_categories: 'terms-options-c005.json',

	gs001: 'terms-options-gs001.json',
	cdc_geographic_scope: 'terms-options-gs001.json',

	g002: 'terms-options-g002.json',
	cdc_geolocation: 'terms-options-g002.json',

	ap99: 'terms-options-ap99.json',
	cdc_subaudiences: 'terms-options-ap99.json',

	o: 'terms-options-o.json',
	cdc_entities: 'terms-options-e001.json',
	cdc_sector: 'terms-options-sr00.json',

};

const DATE_UTC_MIN = 631152000000; // 1/1/1990

class FilterOption {
	value;
	id;
	level;
	value;
	constructor(option) {
		this.value = utils.cleanString(option.value || option.id || option.term_id);
		this.id = utils.cleanString(option.value || option.id || option.term_id);
		this.name = utils.cleanString(option.name || option.text);
		this.label = utils.cleanLabel(option.label || this.name);
		this.level = utils.toNumber(option.level) || 1;
		this.alt = option.alt || '';
	}
}

class TaxOptions {
	widgetId;
	taxonomies = [];
	ready = false;
	onLoadPromise;
	onLoadResolve;
	// reference to core WidgetOptions config object
	widgetOptions;
	widgetModel;

	// widget filters configuration
	filtersConfig = [];

	/**
	 * On construct, fetch all labels for taxonomy filters
	 * @param {WidgetModel} widgetModel
	 */
	constructor(widgetModel) {
		this.widgetModel = widgetModel;
		this.widgetId = widgetModel.id;
		this.widgetOptions = widgetModel.options;
		this.filtersConfig = utils.toArray(this.widgetOptions.filters);
		let fetches = [];

		// on load promise - share resolve and reject
		this.onLoadPromise = new Promise((resolve, reject) => {
			this.onLoadResolve = resolve;
		}).then(() => {
			this.ready = true;
		});

		// @TODO: update when we support more than 1 site
		let site_id = 0;
		if (this.widgetOptions.contentSources.length) {
			// site id can be a number or a Topical Site Context: `23_345`
			let temp_site_id = utils.toNumber(String(this.widgetOptions.contentSources[0].site_id || '').replace(/[-_].*$/,''));
			if (temp_site_id) {
				site_id = temp_site_id;
			} else {
				config.debug && console.error('cc: cannot find site id from sources:', this.widgetOptions.contentSources[0]);
			}
		}

		// if static mode, skip this step
		if (!widgetModel.isStatic) {
			utils.toArray(this.widgetOptions.filters).forEach((filter) => {
				// if filter is a taxonomy, need to fetch the lengend file
				if (filter.id && filter.taxonomy) {
					fetches.push(this.fetchTaxonomy(filter, site_id));
				}
			});
		}

		// look through this.widgetOptions filters, and check for taxonomy legend files we need to fetch

		// when all taxOption fetches are done, this is ready
		Promise.all(fetches).then(() => {
			this.onLoadResolve();
		});
	}

	/**
	 * Return promise that resolves once filterOptions are loaded
	 * @returns Promise
	 */
	whenReady() {
		return this.onLoadPromise;
	}

	/**
	 * Fetches options for an individual taxonomy
	 * @param {object} filter Fitler from WidgetOptions filter
	 * @param {int} siteId
	 * @returns Promise
	 */
	fetchTaxonomy(filter, siteId) {
		return new Promise((resolve) => {
			filter = Object.assign({}, filter);
			let url = false;
			let path = config.taxURL + '/';
			const taxonomy = filter.taxonomy;
			const filterId = filter.id;
			if (GLOBAL_TAXONOMY_FILES[taxonomy]) {
				url = path + GLOBAL_TAXONOMY_FILES[taxonomy];
			} else {
				url = path + 'terms-options-' + siteId + '-' + taxonomy + '.json';
			}
			if (!url) {
				config.debug && console.error('cc:' + this.id + ': cannot get url for tax filter: ', filter);
				return resolve();
			}
			axios({ url })
				.then((response) => {
					if (!Array.isArray(response.data)) {
						config.debug && console.error('cc: tax file isnt array: ', url, taxonomy);
						return resolve();
					}
					// @TODO: this guy here has all of the taxonomies
					this.taxonomies[taxonomy] = response.data;
					return resolve();
				})
				.catch((error) => {
					config.debug && console.error('cc: error fetching: ', url, error);
					return resolve();
				});
		});
	}

	/**
	 * Given the solrContentConfig, figure which solr fields map to each filter
	 * @param {SolrContentConfig} solrContentConfig
	 */
	setSolrContentConfig(solrContentConfig) {
		this.filtersConfig.forEach(filterConfig => {
			let fieldValue = filterConfig.value || filterConfig.id;
			let solrField = solrContentConfig.getSolrField(fieldValue);

			// if filter is based on a post field, get that solrField value
			if (filterConfig.field) {
				solrField = solrContentConfig.getSolrFieldsForPostField(filterConfig.field)
			}
			if ( Array.isArray(solrField) ) {
				solrField = utils.arrayFirst(solrField);
			}
			filterConfig.solr = solrField;

			// taxonomy fields also get a solrParent field
			if (filterConfig.taxonomy) {
				let solrParentField = solrContentConfig.getSolrParentsField(fieldValue);
				if (solrParentField) {
					filterConfig.solrParent = solrParentField;
				}
			}

		});
	}

	/**
	 * Returns necessary SOLR fields to request facets for given filters
	 * Filter Options may sometimes be stored in arrays.
	 * @param {*} solrContentConfig Instance of SolrContentConfig
	 */
	getSolrFields() {
		let solrFields = [];
		this.filtersConfig.forEach(filterConfig => {
			solrFields = solrFields.concat( filterConfig.solr || filterConfig.value || filterConfig.id );
		});
		solrFields = utils.unique(solrFields);
		return solrFields;
	}

	/**
	 * Given a passed set of filter values, return fq SOLR values
	 * @param {Object} filterValues Filter values from the widget
	 */
	getSolrFilterQueries(filterValues) {
		let fqs = [];
		filterValues = Object.assign({}, filterValues);
		for (let filterId in filterValues) {
			let fq;
			let filterOption = filterValues[filterId];
			if (( !filterOption || !filterOption.value ) && (!Array.isArray(filterOption) || !filterOption.length)){
				continue;
			}
			let filterConfig = this.filtersConfig.find(filter => (filter.id === filterId));
			if (!filterConfig) {
				continue;
			}

			// if date filter, special function handles
			if (
				filterConfig.id === WidgetOptions.options.filters.date_y.id ||
				filterConfig.id === WidgetOptions.options.filters.date_ym.id
			) {
				if( Array.isArray(filterOption) ) {
					let fqQueryString = '';
					if( 1 < filterOption.length ) {
						for(let i = 0; i < filterOption.length; i++ ){
							if( 0 < i ) {
								fqQueryString += ' OR ';
							}
							// Add the current term to the filter query string
							fqQueryString += this.getDateSolrFilterQuery(filterConfig, filterOption[i]);
						}
					} else {
						fqQueryString += this.getDateSolrFilterQuery(filterConfig, filterOption[0]);
					}
					fq = fqQueryString;
				} else {
					fq = this.getDateSolrFilterQuery(filterConfig, filterOption);
				}
			} else {
				let solrField = filterConfig.solr;
				let fqQueryString = '';
				if ( Array.isArray(filterOption) ) {
					if( 1 < filterOption.length ) {
						for( let i = 0;  i < filterOption.length; i++ ){
							// If it's not the first term, add OR
							if( 0 < i ) {
								fqQueryString += ' OR ';
							}
							// Add the current term to the filter query string
							fqQueryString += `"${filterOption[i].value}"`;
						}
						fq = solrField + ':' + '((' + fqQueryString +')';
					} else {
						fq = solrField + ':' + filterOption[0].value;
					}
				} else {
					fq = solrField + ':' + filterOption.value;
				}
				// If the filter option value is a string, treat like an array for the DFE options
				if (filterConfig.solrParent) {
					if( Array.isArray(filterOption) ){
						if( 1 < filterOption.length ) {
							fq += ' 0R ' + filterConfig.solrParent + ':' + '(' + fqQueryString + '))';
						} else {
							fq += ' OR ' + filterConfig.solrParent + ':' + filterOption[0].value;
						}
					} else {
						fq += ' OR ' + filterConfig.solrParent + ':' + filterOption.value;
					}
				}
			}
			if (fq) {
				fqs.push(fq);
			}
		}

		return fqs;

	}

	/**
	 * Return a solr filter query for a date range filter
	 * @param {object}       filterConfig Filter config
	 * @param {FilterOption} filterOption Filter option
	 * @return {string|null}
	 */
	getDateSolrFilterQuery(filterConfig, filterOption) {
		let fq = null;
		let minDate;
		let maxDate;
		let date = dayjs(String(filterOption.value));
		let offset = date.utcOffset() / 60;
		if (filterConfig.id === WidgetOptions.options.filters.date_y.id ) {
			minDate = date.startOf('year');
			maxDate = date.endOf('year');
		} else if (filterConfig.id === WidgetOptions.options.filters.date_ym.id) {
			minDate = date.startOf('month');
			maxDate = date.endOf('month');
		} else {
			return fq;
		}

		// GMT FIX: our dates are often saves in GMT, so mindate should start at 00 hours GMT
		// let lower = String(minDate.toISOString()).replace(/T\d{2}/, 'T00');
		// let upper = String(maxDate.toISOString());
		let lower = minDate.year() + '-' + (minDate.month()+1) + '-' + minDate.date() + 'T00:00:00.000Z';
		let upper = maxDate.year() + '-' + (maxDate.month()+1) + '-' + maxDate.date() + 'T23:59:59.999Z';

		fq = filterConfig.solr + ':[' + lower + ' TO ' + upper + ']';
		return fq;
	}

	/**
	 * Given the filter settings, return populated filters for WidgetModel
	 *
	 * @param {object} solrTerms
	 * @returns array
	 */
	loadFilters(solrTerms) {
		// Copy filters config, don't alter original
		let filters = this.filtersConfig.slice();

		solrTerms = Object.assign({}, solrTerms);
		// @TODO: handle non taxonomy fields? Dates?
		for (let filterIndex = filters.length - 1; -1 < filterIndex; filterIndex--) {
			let filter = filters[filterIndex];
			let filterId = filter.id;
			let filterOptions = this.getFilterOptions(filter, solrTerms);
			if ( ! filterOptions ) {
				config.debug && console.error('cc: couldnt load options, removing filter: ', filterId);
				filters.splice(filterIndex, 1);
			} else if ( ! filterOptions.length ) {
				config.debug && console.error('cc: list has no entries for tax: ', filterId);
				filters.splice(filterIndex, 1);
			} else {
				filters[filterIndex].options = filterOptions;
			}
		}
		return filters;
	}

	/**
	 * Given a taxonomy and list of terms ids from SOLR, return all options
	 *
	 * @param {object} filter Filter from WidgetOptions filters
	 * @param {object} solrTerms Facet terms returned from SOLR
	 * @returns {FilterOption[]|false} Full options array from JSON, or false if not available
	 */
	getFilterOptions(filter, solrTerms) {
		let solrField = filter.solr;
		if (!solrTerms[solrField]) {
			return false;
		}
		let filterSolrTerms = utils.toArray(solrTerms[solrField]);

		// weed out the counts returned by SOLR
		filterSolrTerms = filterSolrTerms.filter(item => 'string' === typeof item )
		const filterId = filter.id;
		const taxonomy = filter.taxonomy;
		// all filter terms
		let filterTerms = [];
		// returned filter options
		let filterOptions = [];
		if (taxonomy) {
			if (!this.taxonomies[taxonomy]) {
				config.debug && console.error('cc: cannot get tax options for ', taxonomy);
				return false;
			}
			// loop through all known terms and pick out the ones in our result facet set
			let filterIds = [];
			utils.toArray(this.taxonomies[taxonomy]).forEach(filterTerm => {
				// IDs may be strings or numbers
				if (-1 < filterSolrTerms.indexOf(filterTerm?.id) || -1 < filterSolrTerms.indexOf(String(filterTerm?.id))) {
					if ( -1 === filterIds.indexOf(filterTerm?.id) ) {
						filterIds.push(filterTerm?.id);
						filterOptions.push(new FilterOption(filterTerm));
					}
				}
			});
		} else if (filterId === WidgetOptions.options.filters.language.id ) {
			// Handle language filter
			filterSolrTerms.forEach(term => {
				if (LANGUAGES[term]) {
					filterOptions.push(new FilterOption({
						name: LANGUAGES[term],
						value: term,
					}));
				}
			})
		} else if (
			filterId === WidgetOptions.options.filters.date_y.id ||
			filterId === WidgetOptions.options.filters.date_ym.id
		) {
			// Handle date filters
			return this.getDateFilterOptions(filter, filterSolrTerms);
		}

		// @TODO: Add logic to handle different filter displays / hierarchy configs
		// for now default to alph sorting list
		filterOptions.sort((a, b) => String(a?.name || '').localeCompare( String(b?.name || '')));

		return filterOptions;
	}

	/**
	 * Specifically handles loading options for a date filter
	 * @param {Object} filter          ID / Type of filter
	 * @param {array}  filterSolrTerms Array of available filter terms
	 * @returns FilterOption[];
	 */
	getDateFilterOptions(filter, filterSolrTerms) {
		let filterOptions = [];
		let dateValues = [];
		filterSolrTerms.sort().reverse();

		let filterConfig;
		let dateLabelFormat = 'YYYY';
		let dateValueFormat = 'YYYY';
		if (filter.id === WidgetOptions.options.filters.date_ym.id) {
			filterConfig = WidgetOptions.options.filters.date_ym;
		}
		if (filter.id === WidgetOptions.options.filters.date_y.id) {
			filterConfig = WidgetOptions.options.filters.date_y;
		}
		if (filterConfig) {
			dateLabelFormat = filterConfig.labelFormat;
			dateValueFormat = filterConfig.valueFormat;
		}
		filterSolrTerms.forEach(solrTerm => {
			if (!solrTerm) {
				return;
			}
			// GMT FIX: our dates are often saves in GMT, so strip time off - just process date
			let dateInput = String(solrTerm).replace(/T\d+.*$/, '');
			let date = dayjs(dateInput);

			if (DATE_UTC_MIN > date.valueOf()) {
				return;
			}
			let dateLabel = date.format(dateLabelFormat);
			let dateValue = date.format(dateValueFormat);

			if (-1 < dateValues.indexOf(dateValue)) {
				return;
			}
			dateValues.push(dateValue);

			filterOptions.push(new FilterOption({
				value: dateValue,
				name: dateLabel,
			}));

		});
		return filterOptions;
	}

}

export default TaxOptions;
